import React from "react"
import { Box, Stack, Text } from "@chakra-ui/react"

import Card from "@/components/Card/Card"
import CardBody from "@/components/Card/CardBody"
import CardHeader from "@/components/Card/CardHeader"
import { cn } from "@/lib/utils"
import { reportDetailSectionCardProps } from "@/views/Dashboard/Reports/components/ReportsCardChrome"
import { useReportsDetailColors } from "@/views/Dashboard/Reports/reportsDetailTheme"
import { AUTH_FONT, WIZARD_DESC_CLASS } from "./DemoEntryAnalysisShell"

const REPORT_PRIMARY_FONT = "var(--font-primary)"

const SIMULATOR_FEATURES = [
  {
    title: "Entry filters",
    body:
      "Keep or drop trades by session, regime, volatility, structure, or higher-timeframe context.",
  },
  {
    title: "Stop and target replay",
    body:
      "Replay SL and TP on closed trades using historical candles around each entry.",
  },
  {
    title: "Before vs after metrics",
    body:
      "Compare trade count, win rate, PnL, expectancy, profit factor, and drawdown.",
  },
  {
    title: "Reports to policies",
    body:
      "Load report slices, save simulation runs, and turn results into strategy policies.",
  },
] as const

const SIMULATOR_VS_BACKTEST_ROWS = [
  {
    label: "Tessera Simulator",
    body:
      "Runs on closed trades from your broker account. Tests one change at a time on a fixed history. Grounded in real fills, fees, and timing.",
  },
  {
    label: "Typical strategy backtest",
    body:
      "Replays rules bar by bar on chart data. Can diverge from live fills and discretion. Complements Tessera with logic checks on code.",
  },
] as const

export function DemoSimulatorUpsellPanel(): React.ReactElement {
  const { subtleText } = useReportsDetailColors()

  return (
    <Stack spacing={{ base: 5, md: 6 }} w="100%">
      <Card {...reportDetailSectionCardProps}>
        <CardHeader pb={{ base: 3, md: 4 }}>
          <Box flex="1" minW={{ base: "100%", md: "260px" }}>
            <Text
              fontSize={{ base: "2xl", md: "3xl" }}
              color="#38BBD1"
              fontWeight="normal"
              fontFamily={REPORT_PRIMARY_FONT}
              lineHeight="1.15"
            >
              Simulator
            </Text>
            <Text fontSize="sm" color={subtleText} mt={1} fontFamily={REPORT_PRIMARY_FONT}>
              What-if on closed trades, not live forward testing.
            </Text>
          </Box>
        </CardHeader>
        <CardBody display="flex" flexDirection="column" pt={0} w="full">
          <Text
            fontSize="sm"
            color={subtleText}
            fontFamily={REPORT_PRIMARY_FONT}
            lineHeight="1.6"
            maxW="720px"
          >
            Same entries you already took, with different filters or exit management. See how
            stats would shift. No orders placed and no live-rule forecasts.
          </Text>
        </CardBody>
      </Card>

      <Card {...reportDetailSectionCardProps}>
        <CardHeader pb={{ base: 2, md: 3 }}>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="#38BBD1"
            fontWeight="normal"
            fontFamily={REPORT_PRIMARY_FONT}
          >
            What you can do
          </Text>
        </CardHeader>
        <CardBody pt={0}>
          <Stack spacing={4}>
            {SIMULATOR_FEATURES.map((item) => (
              <Box key={item.title}>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="#38BBD1"
                  fontFamily={REPORT_PRIMARY_FONT}
                  mb={1}
                >
                  {item.title}
                </Text>
                <Text
                  fontSize="sm"
                  color={subtleText}
                  fontFamily={REPORT_PRIMARY_FONT}
                  lineHeight="1.55"
                >
                  {item.body}
                </Text>
              </Box>
            ))}
          </Stack>
        </CardBody>
      </Card>

      <Card {...reportDetailSectionCardProps}>
        <CardHeader pb={{ base: 2, md: 3 }}>
          <Box flex="1" minW="100%">
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="#38BBD1"
              fontWeight="normal"
              fontFamily={REPORT_PRIMARY_FONT}
            >
              Simulator vs backtest
            </Text>
            <Text
              fontSize="sm"
              color={subtleText}
              mt={{ base: 2, md: 3 }}
              fontFamily={REPORT_PRIMARY_FONT}
              lineHeight="1.55"
            >
              Complements TradingView and broker backtests.
            </Text>
          </Box>
        </CardHeader>
        <CardBody pt={0}>
          <Stack spacing={4}>
            {SIMULATOR_VS_BACKTEST_ROWS.map((row) => (
              <Box key={row.label}>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="#38BBD1"
                  fontFamily={REPORT_PRIMARY_FONT}
                  mb={1}
                >
                  {row.label}
                </Text>
                <Text
                  fontSize="sm"
                  color={subtleText}
                  fontFamily={REPORT_PRIMARY_FONT}
                  lineHeight="1.55"
                >
                  {row.body}
                </Text>
              </Box>
            ))}
          </Stack>
        </CardBody>
      </Card>

      <Card {...reportDetailSectionCardProps}>
        <CardHeader pb={{ base: 2, md: 3 }}>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="#38BBD1"
            fontWeight="normal"
            fontFamily={REPORT_PRIMARY_FONT}
          >
            How it fits your workflow
          </Text>
        </CardHeader>
        <CardBody pt={0}>
          <Text fontSize="sm" color={subtleText} fontFamily={REPORT_PRIMARY_FONT} lineHeight="1.6">
            Find leaks in Entry Analysis, test one change in the simulator, then save the run
            and attach it to a strategy policy or Strategy Workspace canvas.
          </Text>
          <p className={cn(AUTH_FONT, WIZARD_DESC_CLASS, "mt-4 text-sm leading-relaxed")}>
            Subscribe to run simulations on connected accounts and load report slices into the
            simulator.
          </p>
        </CardBody>
      </Card>
    </Stack>
  )
}
